import type { MouseEvent, ReactNode } from 'react';
import { Col, Dropdown, Row } from 'react-bootstrap';
import { FileExcel } from 'react-bootstrap-icons';

import { Pagination } from '../Pagination';

interface PaginatedGridData {
  currentPage: number;
  pageCount: number;
  rowCount: number;
  data?: Array<unknown>;
}

interface PaginatedGridLayoutProps {
  grid: ReactNode;
  data: PaginatedGridData;
  page: number;
  onPage: (page: number) => void;
  pagedTop?: boolean;
  entriesLabel: string;
  excelLabel: string;
  exportable?: boolean;
  exportFn?: () => Promise<void>;
}

interface EntriesExportControlProps {
  legend: string;
  excelLabel: string;
  exportable?: boolean;
  onExport?: () => Promise<void>;
  asDropdown: boolean;
}

function EntriesExportControl({
  legend,
  excelLabel,
  exportable,
  onExport,
  asDropdown
}: EntriesExportControlProps) {
  const exportGrid = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (onExport) {
      void onExport();
    }
  };

  if (!asDropdown) {
    return <span>{legend}</span>;
  }

  return (
    <Dropdown>
      <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
        {legend}
      </Dropdown.Toggle>
      {exportable && (
        <Dropdown.Menu>
          <Dropdown.Item href="#" onClick={exportGrid}>
            <FileExcel className="me-1" />
            {excelLabel}
          </Dropdown.Item>
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}

export function PaginatedGridLayout({
  grid,
  data,
  page,
  onPage,
  pagedTop,
  entriesLabel,
  excelLabel,
  exportable,
  exportFn
}: PaginatedGridLayoutProps) {
  const hasData = data.data !== undefined && data.data.length > 0;
  const legend = `${data.rowCount} ${entriesLabel}`;
  const pagination = (
    <Pagination
      {...data}
      lastPage={data.pageCount}
      total={data.rowCount}
      page={page}
      onPage={onPage}
    />
  );
  if (pagedTop) {
    return (
      <>
        {hasData && (
          <Row className="mt-2">
            <Col>
              <div className=" me-auto ">
                <EntriesExportControl
                  legend={legend}
                  excelLabel={excelLabel}
                  exportable={exportable}
                  onExport={exportFn}
                  asDropdown={Boolean(exportable)}
                />
              </div>
            </Col>
            <Col>
              <div className="ms-auto">{pagination}</div>
            </Col>
          </Row>
        )}
        {grid}
      </>
    );
  }

  return (
    <>
      {grid}
      {hasData && (
        <Row className="mt-2">
          <Col>
            <div className="me-auto">
              <EntriesExportControl
                legend={legend}
                excelLabel={excelLabel}
                exportable={exportable}
                onExport={exportFn}
                asDropdown={true}
              />
            </div>
          </Col>
          <Col>
            <div className="ms-auto">{pagination}</div>
          </Col>
        </Row>
      )}
    </>
  );
}
